import React from 'react';
import {Box, Typography} from '@mui/material';
import PageBackButton from './page-back-button';
import {
    ActionsSkeleton,
    AlertsSkeleton,
    BackSkeleton,
    BottomBarSkeleton,
    BreadcrumbsSkeleton,
    ContentSkeleton,
    FiltersSkeleton,
    resolveSlotLoading,
    TabsSkeleton,
    TitleSkeleton,
} from './skeletons';
import type {PageShellProps, PageShellSlot} from './types';

const SLOT_KEYS = [
    'back',
    'title',
    'breadcrumbs',
    'actions',
    'alerts',
    'filters',
    'tabs',
    'content',
    'bottomBar',
] as const satisfies readonly PageShellSlot[];

/**
 * Shared panel page chrome: back + title/subtitle + actions, breadcrumbs, filters, tabs, content, bottom bar.
 */
const PageShell = ({
    title,
    subtitle,
    back = null,
    breadcrumbs = null,
    actions = null,
    alerts = null,
    filters = null,
    tabs = null,
    bottomBar = null,
    children,
    loading,
    contentSkeletonVariant = 'list',
    sx,
}: PageShellProps): React.ReactElement => {
    const slotLoading = resolveSlotLoading(loading, SLOT_KEYS);
    const showHeader = title != null || subtitle != null || Boolean(back) || Boolean(actions);

    return (
        <Box
            sx={[
                {
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            {showHeader && (
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: {xs: 'flex-start', sm: 'center'},
                        flexWrap: 'wrap',
                        gap: 1.5,
                        mt: 0,
                        mb: breadcrumbs || slotLoading.breadcrumbs ? 1 : 3,
                    }}
                >
                    <Box sx={{display: 'flex', alignItems: 'center', minWidth: 0, flex: '1 1 auto', gap: 0.5}}>
                        {slotLoading.back
                            ? <BackSkeleton active/>
                            : back
                                ? <PageBackButton {...back}/>
                                : null}
                        {slotLoading.title
                            ? <TitleSkeleton active subtitle={Boolean(subtitle)}/>
                            : (
                                <Box sx={{minWidth: 0}}>
                                    {title != null && (
                                        <Typography variant="h6" fontWeight={600} component="h1">
                                            {title}
                                        </Typography>
                                    )}
                                    {subtitle != null && (
                                        <Typography variant="body2" color="text.secondary" sx={{mt: 0.25}}>
                                            {subtitle}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                    </Box>
                    {slotLoading.actions
                        ? <ActionsSkeleton active/>
                        : actions}
                </Box>
            )}

            {slotLoading.breadcrumbs
                ? <BreadcrumbsSkeleton active/>
                : breadcrumbs}

            {slotLoading.alerts
                ? <AlertsSkeleton active/>
                : alerts
                    ? <Box sx={{mb: 2, width: '100%'}}>{alerts}</Box>
                    : null}

            {slotLoading.filters
                ? <FiltersSkeleton active/>
                : filters
                    ? <Box sx={{mb: 3, width: '100%'}}>{filters}</Box>
                    : null}

            {slotLoading.tabs
                ? <TabsSkeleton active/>
                : tabs
                    ? <Box sx={{mb: 2, width: '100%'}}>{tabs}</Box>
                    : null}

            <Box sx={{width: '100%', flex: '1 1 auto'}}>
                {slotLoading.content
                    ? <ContentSkeleton active variant={contentSkeletonVariant}/>
                    : children}
            </Box>

            {slotLoading.bottomBar
                ? <BottomBarSkeleton active/>
                : bottomBar
                    ? (
                        <Box
                            sx={{
                                position: 'sticky',
                                bottom: 0,
                                mt: 2,
                                py: 1.5,
                                bgcolor: 'background.paper',
                                borderTop: 1,
                                borderColor: 'divider',
                                zIndex: 1,
                            }}
                        >
                            {bottomBar}
                        </Box>
                    )
                    : null}
        </Box>
    );
};

export default PageShell;
